<!-- XYLO Copilot : un chat flottant qui pilote l'interface de l'ERP.
     L'utilisateur écrit ce qu'il veut ; Claude (serveur, /api/copilot/chat) reçoit la
     page lue en texte et répond par des actions que ce composant exécute dans la
     page (services/copilot.js) : on voit le curseur, les champs se remplir, les pages
     changer. Monté une fois dans le layout principal ; ne s'affiche que si le
     serveur autorise le compte (phase d'essai : Chady). Textes en anglais, comme
     le reste de l'ERP. -->
<template>
  <div v-if="allowed" id="cp-root">
    <button class="cp-launcher" :class="{ 'is-open': open, 'is-busy': running }" title="XYLO Copilot" @click="toggle">
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z"/><path d="M19 17l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z"/>
      </svg>
    </button>

    <transition name="cp-slide">
      <div v-if="open" class="cp-panel">
        <div class="cp-head">
          <span class="cp-title">
            <span class="cp-dot" :class="{ on: running }"></span>
            XYLO Copilot
            <small v-if="modelLabel">{{ modelLabel }}</small>
          </span>
          <div class="cp-head-actions">
            <button title="New conversation" :disabled="running" @click="reset">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 3v6h6"/></svg>
            </button>
            <button title="Close" @click="open = false">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
        </div>

        <div ref="body" class="cp-body">
          <div v-if="!messages.length" class="cp-empty">
            <p>Tell me what to do in the ERP and watch it happen.</p>
            <p class="cp-examples">
              <span @click="use('Open the contacts list and find the client ')">Find a client</span>
              <span @click="use('Add a new contact: ')">Add a contact</span>
              <span @click="use('Create a quotation for ')">New quotation</span>
            </p>
            <p v-if="!keyConfigured" class="cp-warn">No Claude API key is configured (Settings → AI).</p>
          </div>
          <div v-for="(m, i) in messages" :key="i" class="cp-msg" :class="'cp-' + m.role">
            <div v-if="m.role === 'thinking'" class="cp-thinking" :title="m.text">{{ shorten(m.text, 220) }}</div>
            <div v-else-if="m.role === 'action'" class="cp-action" :class="{ 'is-error': m.error }">
              <span class="cp-action-n">{{ m.n }}</span>
              <span class="cp-action-text">{{ m.text }}</span>
              <span v-if="m.error" class="cp-action-err">{{ m.error }}</span>
            </div>
            <div v-else class="cp-bubble" v-html="render(m.text)"></div>
          </div>
          <div v-if="running" class="cp-status"><span class="cp-spinner"></span>{{ status }}</div>
        </div>

        <div class="cp-foot">
          <textarea
            ref="input"
            v-model="draft"
            rows="2"
            placeholder="What should I do? (Enter to send, Shift+Enter for a new line)"
            :disabled="running"
            @keydown.enter.exact.prevent="send"></textarea>
          <button v-if="running" class="cp-btn cp-stop" @click="stop">Stop</button>
          <button v-else class="cp-btn cp-send" :disabled="!draft.trim()" @click="send">Send</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from '@/axios.js'
import { snapshotPage, executeAction, buildSitemap, describeAction, pageToText, hideCursor } from '@/services/copilot.js'

const MAX_ROUNDS = 60 // garde-fou côté navigateur ; le serveur en a un aussi

export default {
  name: 'CopilotPanel',
  data () {
    return {
      allowed: false,
      keyConfigured: true,
      model: '',
      open: false,
      messages: [],
      draft: '',
      running: false,
      stopped: false,
      status: '',
      sessionId: null,
      sitemapSent: false,
      stepCount: 0
    }
  },
  computed: {
    modelLabel () { return this.model ? this.model.replace(/^claude-/, '') : '' }
  },
  created () {
    axios.get('/copilot/access')
      .then(r => {
        const d = r.data || {}
        this.allowed = !!d.allowed
        if (this.allowed) document.body.classList.add('has-copilot')
        this.model = d.model || ''
        this.keyConfigured = d.key_configured !== false
      })
      .catch(() => { this.allowed = false })
  },
  methods: {
    toggle () {
      this.open = !this.open
      if (this.open) this.$nextTick(() => { if (this.$refs.input) this.$refs.input.focus() })
    },
    use (text) {
      this.draft = text
      this.$nextTick(() => { if (this.$refs.input) this.$refs.input.focus() })
    },
    shorten (s, n) { return s && s.length > n ? `${s.slice(0, n)}…` : s },
    render (text) {
      const esc = String(text || '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      return esc
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
        .replace(/^- (.*)$/gm, '• $1')
        .replace(/\n/g, '<br>')
    },
    push (role, text, extra) {
      this.messages.push(Object.assign({ role, text }, extra || {}))
      this.$nextTick(() => { const b = this.$refs.body; if (b) b.scrollTop = b.scrollHeight })
    },
    async send () {
      const text = this.draft.trim()
      if (!text || this.running) return
      this.draft = ''
      this.push('user', text)
      this.running = true
      this.stopped = false
      this.stepCount = 0
      this.status = 'Thinking…'
      const payload = { session_id: this.sessionId, text, page: snapshotPage(this.$router) }
      if (!this.sitemapSent || !this.sessionId) payload.sitemap = buildSitemap()
      try {
        await this.runLoop(payload)
      } finally {
        this.running = false
        this.status = ''
        hideCursor()
        this.$nextTick(() => { if (this.$refs.input) this.$refs.input.focus() })
      }
    },
    async runLoop (payload) {
      for (let round = 0; round < MAX_ROUNDS; round++) {
        let resp = null
        try {
          resp = (await axios.post('/copilot/chat', payload, { timeout: 300000 })).data
        } catch (e) {
          const msg = (e.response && e.response.data && e.response.data.message) || e.message || 'request failed'
          this.push('assistant', `⚠️ ${msg}`)
          return
        }
        this.sessionId = resp.session_id
        this.sitemapSent = true
        if (resp.thinking) this.push('thinking', resp.thinking)
        if (resp.text) this.push('assistant', resp.text)
        if (!resp.actions || !resp.actions.length) return

        const results = []
        for (let i = 0; i < resp.actions.length; i++) {
          const a = resp.actions[i]
          if (this.stopped) {
            results.push({ tool_use_id: a.id, content: 'Cancelled by the user.', is_error: true })
            continue
          }
          this.stepCount += 1
          const label = describeAction(a)
          this.status = label
          const entry = { n: this.stepCount, error: '' }
          this.push('action', label, entry)
          const out = await executeAction(a, this.$router)
          if (out.error) this.messages[this.messages.length - 1].error = out.text
          const last = i === resp.actions.length - 1
          let content = out.text
          if (out.page && (last || out.error)) content += `\n\n[Page after the action]\n${pageToText(out.page)}`
          results.push({ tool_use_id: a.id, content, is_error: !!out.error })
        }
        payload = { session_id: this.sessionId, tool_results: results, cancelled: this.stopped }
        if (this.stopped) {
          await axios.post('/copilot/chat', payload).catch(() => {})
          this.push('assistant', 'Stopped.')
          return
        }
        this.status = 'Thinking…'
      }
      this.push('assistant', `Stopped after ${MAX_ROUNDS} rounds.`)
    },
    stop () {
      this.stopped = true
      this.status = 'Stopping…'
    },
    async reset () {
      if (this.sessionId) await axios.post('/copilot/reset', { session_id: this.sessionId }).catch(() => {})
      this.sessionId = null
      this.sitemapSent = false
      this.messages = []
      this.stepCount = 0
    }
  }
}
</script>

<style>
/* Non scopé : le curseur fantôme et la surbrillance vivent hors du composant. */
#cp-root { font-family: inherit; }
/* Le bouton ✦ prend le coin bas droit ; le « retour en haut » (vue-backtotop, style en ligne) monte au-dessus. */
body.has-copilot .vue-back-to-top { bottom: 88px !important; right: 30px !important; }
.cp-launcher {
  position: fixed; right: 24px; bottom: 24px; z-index: 9100;
  width: 50px; height: 50px; border-radius: 50%; border: 0; cursor: pointer;
  background: #333b34; color: #fff; box-shadow: 0 6px 18px rgba(0,0,0,.25);
  display: flex; align-items: center; justify-content: center; transition: transform .15s, background .2s;
}
.cp-launcher:hover { transform: scale(1.06); }
.cp-launcher.is-open { background: #1f241f; }
.cp-launcher.is-busy { animation: cp-glow 1.4s ease-in-out infinite; }
@keyframes cp-glow { 0%,100% { box-shadow: 0 6px 18px rgba(0,0,0,.25); } 50% { box-shadow: 0 0 0 10px rgba(51,59,52,.18); } }

.cp-panel {
  position: fixed; right: 24px; bottom: 86px; z-index: 9100;
  width: 410px; max-width: calc(100vw - 32px); height: 600px; max-height: calc(100vh - 180px);
  background: #fff; border-radius: 12px; box-shadow: 0 14px 40px rgba(0,0,0,.28);
  display: flex; flex-direction: column; overflow: hidden; color: #2c2c2c;
}
.theme-dark .cp-panel { background: #262c49; color: #c2c6dc; }
.cp-slide-enter-active, .cp-slide-leave-active { transition: all .18s ease; }
.cp-slide-enter, .cp-slide-leave-to { opacity: 0; transform: translateY(12px); }

.cp-head { display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; background: #333b34; color: #fff; }
.cp-title { display: flex; align-items: center; gap: 8px; font-weight: 600; }
.cp-title small { font-weight: 400; opacity: .7; font-size: 11px; }
.cp-dot { width: 8px; height: 8px; border-radius: 50%; background: #9aa39b; }
.cp-dot.on { background: #28c76f; box-shadow: 0 0 0 4px rgba(40,199,111,.25); animation: cp-blink 1s infinite; }
@keyframes cp-blink { 50% { opacity: .4; } }
.cp-head-actions button { background: transparent; border: 0; color: #fff; cursor: pointer; padding: 4px; opacity: .85; }
.cp-head-actions button:hover { opacity: 1; }
.cp-head-actions button:disabled { opacity: .3; cursor: default; }

.cp-body { flex: 1; overflow-y: auto; padding: 12px; font-size: 13px; line-height: 1.45; }
.cp-empty { color: #777; text-align: center; margin-top: 40px; }
.cp-examples span { display: inline-block; margin: 4px; padding: 4px 10px; border: 1px solid #ccc; border-radius: 14px; cursor: pointer; font-size: 12px; }
.cp-examples span:hover { background: #f1f3f1; }
.cp-warn { color: #ff9f43; margin-top: 16px; }
.cp-msg { margin-bottom: 8px; }
.cp-bubble { display: inline-block; max-width: 92%; padding: 8px 11px; border-radius: 10px; background: #f1f3f1; white-space: normal; word-break: break-word; }
.theme-dark .cp-bubble { background: #10163a; }
.cp-user { text-align: right; }
.cp-user .cp-bubble { background: #333b34; color: #fff; }
.cp-bubble code { background: rgba(0,0,0,.08); padding: 0 4px; border-radius: 3px; font-size: 12px; }
.cp-thinking { color: #8a8f8b; font-style: italic; font-size: 12px; padding: 2px 6px; border-left: 2px solid #d5d8d5; }
.cp-action { display: flex; align-items: baseline; gap: 8px; font-size: 12px; color: #4b544c; padding: 2px 6px; }
.cp-action-n { min-width: 18px; height: 18px; border-radius: 9px; background: #e3e7e3; color: #333b34; font-size: 10px; display: inline-flex; align-items: center; justify-content: center; }
.cp-action.is-error .cp-action-n { background: #ffe0e0; color: #c0392b; }
.cp-action-err { color: #c0392b; }
.cp-status { display: flex; align-items: center; gap: 8px; color: #333b34; font-size: 12px; margin-top: 6px; }
.cp-spinner { width: 12px; height: 12px; border: 2px solid #c9cfca; border-top-color: #333b34; border-radius: 50%; animation: cp-spin .8s linear infinite; }
@keyframes cp-spin { to { transform: rotate(360deg); } }

.cp-foot { display: flex; gap: 8px; padding: 10px; border-top: 1px solid #e6e8e6; align-items: stretch; }
.theme-dark .cp-foot { border-top-color: #3b4260; }
.cp-foot textarea { flex: 1; resize: none; border: 1px solid #d7dbd8; border-radius: 8px; padding: 8px 10px; font: inherit; font-size: 13px; outline: none; }
.cp-foot textarea:focus { border-color: #333b34; }
.theme-dark .cp-foot textarea { background: #10163a; color: #c2c6dc; border-color: #3b4260; }
.cp-btn { border: 0; border-radius: 8px; padding: 0 14px; cursor: pointer; font-weight: 600; font-size: 13px; }
.cp-send { background: #333b34; color: #fff; }
.cp-send:disabled { opacity: .4; cursor: default; }
.cp-stop { background: #ea5455; color: #fff; }

/* Curseur fantôme : suit les actions de Claude dans la page. */
#cp-cursor {
  position: fixed; left: 0; top: 0; z-index: 100000; pointer-events: none; opacity: 0;
  transform: translate(-100px, -100px); transition: transform .45s cubic-bezier(.2,.7,.2,1), opacity .25s;
  filter: drop-shadow(0 2px 3px rgba(0,0,0,.35));
}
#cp-cursor svg { position: absolute; left: -4px; top: -2px; }
#cp-cursor .cp-cursor-ring { position: absolute; left: -14px; top: -14px; width: 28px; height: 28px; border-radius: 50%; border: 2px solid #333b34; opacity: 0; transform: scale(.4); }
#cp-cursor.is-click .cp-cursor-ring { animation: cp-ring .5s ease-out; }
@keyframes cp-ring { 0% { opacity: .9; transform: scale(.4); } 100% { opacity: 0; transform: scale(1.6); } }
.cp-highlight { outline: 2px solid #28c76f !important; outline-offset: 2px; border-radius: 4px; transition: outline-color .3s; box-shadow: 0 0 0 6px rgba(40,199,111,.18) !important; }
</style>
